import { SERVICE_TYPE } from '@/api/type/commonTypes';
import { OPTION_TYPE } from '@/api/type/types';
import { useAppSelector } from '@/stores/hooks';
import { service } from '@/stores/slices/service-regist/serviceSlices';
import { fonts } from '@/theme/fonts';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

interface AddOrderCustomOptionProps {
  isEdited: boolean;
  customData: OPTION_TYPE[];
  serviceType: SERVICE_TYPE;
}
const AddOrderCustomOption = ({
  isEdited,
  customData,
  serviceType,
}: AddOrderCustomOptionProps) => {
  const [optionValue, setOptionValue] = useState<string>('');
  const dispatch = useDispatch();
  const { putPartnerServiceOptions: putData } = useAppSelector(
    (state) => state.service
  );
  const { t } = useTranslation();

  const onClickRemoveCustomOption = useCallback(
    (optionId: string) => () => {
      const currentPutData = putData?.[serviceType];

      if (!isEdited || !currentPutData) return;

      const updatedCustomOptions =
        currentPutData.customOptions?.filter((opt) => opt.id !== optionId) ??
        [];

      dispatch(
        service.setPutPartnerServiceOptions({
          serviceType,
          data: {
            ...currentPutData,
            customOptions: updatedCustomOptions,
          },
        })
      );
    },
    [dispatch, isEdited, putData, serviceType]
  );

  const onChangeCustomOptionValue = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setOptionValue(e.target.value);
    },
    []
  );

  const onAddCustomOption = useCallback(() => {
    const currentPutData = putData?.[serviceType];

    if (!isEdited || !currentPutData) return;
    if (optionValue.trim() === '') return;

    const newOption: OPTION_TYPE = {
      id: `temp-${Date.now()}-${Math.random()}`,
      name: optionValue,
    };

    const updatedCustomOptions = [
      ...(currentPutData.customOptions ?? []),
      newOption,
    ];

    dispatch(
      service.setPutPartnerServiceOptions({
        serviceType,
        data: {
          ...currentPutData,
          customOptions: updatedCustomOptions,
        },
      })
    );

    setOptionValue('');
  }, [dispatch, isEdited, optionValue, putData, serviceType]);

  useEffect(() => {
    if (!isEdited) {
      setOptionValue('');
    }
  }, [isEdited]);

  return (
    <Container>
      <AddSection data-disabled={!isEdited || customData.length >= 10}>
        <Input
          placeholder={t('enterTheServiceNameAndClickThe')}
          value={optionValue}
          onChange={onChangeCustomOptionValue}
          disabled={customData.length >= 10}
        />
        <AddButton
          onClick={onAddCustomOption}
          disabled={customData.length >= 10}
        >
          +
        </AddButton>
      </AddSection>
      <OptionWrap>
        {customData.map((item) => (
          <Option key={item.id}>
            {item.name}
            {isEdited && (
              <Xbutton onClick={onClickRemoveCustomOption(item.id)}>X</Xbutton>
            )}
          </Option>
        ))}
      </OptionWrap>
    </Container>
  );
};

export default AddOrderCustomOption;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const AddSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  &[data-disabled='true'] {
    pointer-events: none;
    opacity: 0.5;
  }
`;

const Input = styled.input`
  padding: 0 16.5px;
  text-align: center;
  height: 32px;
  width: 315px;
  box-sizing: border-box;
  border: 1px solid #d4d4d4;
  background: #ffffff;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  color: #5f5f5f;
  font-weight: ${fonts.weight.extraLight};
  font-size: 12px;
  border-radius: 99px;
  outline: none;
`;

const AddButton = styled.button`
  height: 34px;
  width: 75px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #5b4be8;
  border-radius: 99px;
  font-weight: ${fonts.weight.bold};
  font-size: 16px;
  line-height: 140%;
  color: #ffffff;
  box-shadow: 3px 3px 3px rgba(0, 0, 0, 0.25);
  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const OptionWrap = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

const Option = styled.div`
  position: relative;
  height: 32px;
  padding: 0 29px;
  border-radius: 99px;
  background: #5b4be8;
  color: #fffbfb;
  font-size: 14px;
  line-height: 140%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Xbutton = styled.button`
  position: absolute;
  top: -4px;
  right: -1px;
  font-weight: ${fonts.weight.bold};
  font-size: 10px;
  line-height: 140%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0e0e0e;
  border-radius: 99px;
  width: 15px;
  height: 15px;
  color: #fffbfb;
`;
